import React, {
    useState, useRef, useImperativeHandle, forwardRef, useEffect,
} from 'react';
import { Box } from '@mui/material';
import RazProgressSVG from '../../../components/UI/SVG_IMGs/RazProgressSVG';
import PoweredByDQDefault from '../../../assets/images/PoweredByDQ_Default.png';
import useWidgetStyles from '../../../assets/styles/CpaFloatingChatWidgetStyles';

/**
 * Floating chat widget for the public Powered by DQ site — a copy of the
 * /pbdq/apply-v2 widget (PbdqSignup/components/SignupFloatingChatWidget.comp.jsx,
 * itself a copy of CurationApplication/components/CpaFloatingChatWidget.comp.jsx),
 * kept separate so each site's widget can change independently.
 *
 * This is a faithful re-implementation of the program-creation preview's
 * floating chat bubble (button → animated panel reveal → collapse) so the look
 * and motion match exactly across the app.
 *
 * The panel BODY is supplied via `children` — here it hosts the DQ Agent
 * conversation (PbdqAgentChatFlow), so "Talk to the DQ Agent" opens in place
 * instead of navigating to /powered-by-dq/chatbot.
 *
 * Imperative API (via ref): { open(), close(), isOpen() } so a page can open
 * the chat from any trigger.
 */
// Matches sdsColors (solutionsDesignSystem.js) so this widget's own light
// lock reads as the same design system as the rest of the PBDQ site, not
// just a generic light theme.
const LIGHT_VARS = {
    '--brand-surface': '#ffffff',
    '--brand-border': '#E3E2E0',
    '--brand-text': '#2B2C2E',
    '--brand-text-muted': '#4A4C50',
};

const PbdqFloatingChatWidget = forwardRef(({ children, startOpen = false, hidden = false, hideToggle = false, onOpenChange }, ref) => {
    const classes = useWidgetStyles();
    const PoweredByDQ = PoweredByDQDefault;
    const [isChatOpen, setIsChatOpen] = useState(false);        // controls DOM mount
    const [chatAnimPhase, setChatAnimPhase] = useState('idle'); // drives the BUTTON animation
    const [panelPhase, setPanelPhase] = useState('idle');       // drives the PANEL clip-path
    // Bumped each time an open() arrives while the panel is already open; the
    // value is the attention ring's key, so repeat clicks restart its run.
    const [attentionKey, setAttentionKey] = useState(0);

    const chatTimersRef = useRef([]);
    const chatOpenRafRef = useRef(null);
    const panelRef = useRef(null);

    const clearChatTimers = () => {
        chatTimersRef.current.forEach((t) => clearTimeout(t));
        chatTimersRef.current = [];
        if (chatOpenRafRef.current) { cancelAnimationFrame(chatOpenRafRef.current); chatOpenRafRef.current = null; }
    };

    // Inline styles for the toggle bubble at each animation phase (mirrors the
    // program-creation widget's getBubbleStyle).
    const getBubbleStyle = () => {
        const PANEL_W = 400;
        const BTN = 56;
        const HDR = 67;
        const ICON_FROM_LEFT = 35;
        const actualPanelH = Math.min(window.innerHeight - 48, 720);
        const riseY = Math.max(0, actualPanelH - HDR / 2 - BTN / 2);
        const slideX = (24 + PANEL_W - ICON_FROM_LEFT) - (24 + BTN / 2);
        switch (chatAnimPhase) {
            case 'pre-open':
                return { pointerEvents: 'none', transition: 'none' };
            case 'opening':
                return {
                    transform: `translateY(-${riseY}px)`,
                    transition: 'transform 1.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: 1,
                    pointerEvents: 'none',
                    zIndex: 1302,
                };
            case 'sliding':
                return {
                    transform: `translateY(-${riseY}px) translateX(-${slideX}px) scale(0.68)`,
                    transition: 'transform 0.55s ease, opacity 0.45s ease',
                    opacity: 0,
                    pointerEvents: 'none',
                    zIndex: 1302,
                };
            case 'open':
                return { transform: 'scale(0)', opacity: 0, pointerEvents: 'none', transition: 'none' };
            default:
                return {};
        }
    };

    const openChat = () => {
        clearChatTimers();
        if (onOpenChange) onOpenChange(true);
        setIsChatOpen(true);
        setPanelPhase('pre-open');
        setChatAnimPhase('pre-open');
        chatOpenRafRef.current = requestAnimationFrame(() => {
            chatOpenRafRef.current = requestAnimationFrame(() => {
                setPanelPhase('opening');
                setChatAnimPhase('opening');
                chatOpenRafRef.current = null;
                const t1 = setTimeout(() => {
                    setPanelPhase('sliding');
                    setChatAnimPhase('sliding');
                    const t2 = setTimeout(() => {
                        setPanelPhase('open');
                        setChatAnimPhase('open');
                    }, 550);
                    chatTimersRef.current.push(t2);
                }, 1300);
                chatTimersRef.current.push(t1);
            });
        });
    };

    const closeChat = () => {
        clearChatTimers();
        if (onOpenChange) onOpenChange(false);
        setPanelPhase('closing');
        setChatAnimPhase('idle');
        setAttentionKey(0);
        const t = setTimeout(() => { setIsChatOpen(false); setPanelPhase('idle'); }, 1600);
        chatTimersRef.current.push(t);
    };

    useImperativeHandle(ref, () => ({
        // Already open → don't re-run the reveal; flash the border instead so
        // the trigger the user just clicked visibly points at the open panel.
        open: () => {
            if (!isChatOpen) openChat();
            else if (panelPhase === 'open') setAttentionKey((k) => k + 1);
        },
        close: () => { if (isChatOpen) closeChat(); },
        isOpen: () => isChatOpen,
    }));

    // Optionally open on mount.
    useEffect(() => {
        if (startOpen) openChat();
        return () => clearChatTimers();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const showContent = panelPhase === 'open' || panelPhase === 'closing';

    return (
        <>
            {isChatOpen && (
                <Box
                    ref={panelRef}
                    className={`${classes.chatFloatingPanel}${panelPhase !== 'idle' ? ` phase-${panelPhase}` : ''}`}
                    style={{ ...LIGHT_VARS, ...(hidden ? { display: 'none' } : {}) }}
                >
                    {attentionKey > 0 && (
                        <Box
                            key={attentionKey}
                            className={classes.attentionRing}
                            onAnimationEnd={(e) => { if (e.target === e.currentTarget) setAttentionKey(0); }}
                        />
                    )}
                    <Box className={classes.header}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <Box className={classes.brandIcon}>
                                <RazProgressSVG color="var(--pbdq-accent, #FF6900)" style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 0 1.5px var(--pbdq-accent, #FF6900))' }} />
                            </Box>
                            <Box style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--brand-text, #111)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>DQ Agent</span>
                                <span style={{ fontSize: '12px', color: '#999', lineHeight: 1.3 }}>Here to help you</span>
                            </Box>
                        </Box>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, marginLeft: '12px' }}>
                            <img src={PoweredByDQ} alt="Powered by DQ" style={{ height: '32px', flexShrink: 0 }} />
                            <Box
                                onClick={closeChat}
                                className={classes.closeBtn}
                                title="Close chat"
                                aria-label="Close chat"
                            >
                                <span className={classes.minimizeIcon}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                </span>
                                <span className={classes.closeIcon}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </span>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        className={classes.body}
                        sx={{ opacity: showContent ? 1 : 0, transition: showContent ? 'opacity 0.8s ease 0.4s' : 'none' }}
                    >
                        {children}
                    </Box>
                </Box>
            )}

            {!hideToggle && (
                <Box
                    className={`${classes.chatToggleBtn}${chatAnimPhase === 'idle' ? ' btn-idle' : ''}`}
                    style={{ ...LIGHT_VARS, ...getBubbleStyle(), ...(hidden ? { display: 'none' } : {}) }}
                    onClick={openChat}
                >
                    <RazProgressSVG color="var(--pbdq-accent, #FF6900)" style={{ width: '80%', height: '80%', filter: 'drop-shadow(0 0 1.5px var(--pbdq-accent, #FF6900))' }} />
                </Box>
            )}
        </>
    );
});

PbdqFloatingChatWidget.displayName = 'PbdqFloatingChatWidget';

export default PbdqFloatingChatWidget;
